/**
 * Expense Form Reducer
 * Manages expense form state using useReducer pattern
 */

// Action Types
export const EXPENSE_FORM_ACTION_TYPES = {
  // Field Updates
  SET_DESCRIPTION: "SET_DESCRIPTION",
  SET_EXPENSE_DATE: "SET_EXPENSE_DATE",
  SET_CATEGORY: "SET_CATEGORY",
  SET_CURRENCY: "SET_CURRENCY",
  SET_SUB_TOTAL: "SET_SUB_TOTAL",
  SET_TAX: "SET_TAX",
  SET_AMOUNT: "SET_AMOUNT",
  SET_IS_BILLABLE: "SET_IS_BILLABLE",
  SET_USER: "SET_USER",
  SET_CLIENT: "SET_CLIENT",
  SET_PROJECT: "SET_PROJECT",
  SET_COMPANY: "SET_COMPANY",
  SET_NOTE: "SET_NOTE",
  SET_FILE_ATTACHMENT: "SET_FILE_ATTACHMENT",
  SET_FILE_ATTACHMENT_NAME: "SET_FILE_ATTACHMENT_NAME",
  
  // Dropdown Search Terms
  SET_CURRENCY_SEARCH_TERM: "SET_CURRENCY_SEARCH_TERM",
  SET_USER_SEARCH_TERM: "SET_USER_SEARCH_TERM",
  SET_CLIENT_SEARCH_TERM: "SET_CLIENT_SEARCH_TERM",
  SET_PROJECT_SEARCH_TERM: "SET_PROJECT_SEARCH_TERM",
  SET_COMPANY_SEARCH_TERM: "SET_COMPANY_SEARCH_TERM",
  
  // Dropdown Open States
  SET_CURRENCY_DROPDOWN_OPEN: "SET_CURRENCY_DROPDOWN_OPEN",
  SET_CATEGORY_DROPDOWN_OPEN: "SET_CATEGORY_DROPDOWN_OPEN",
  SET_USER_DROPDOWN_OPEN: "SET_USER_DROPDOWN_OPEN",
  SET_CLIENT_DROPDOWN_OPEN: "SET_CLIENT_DROPDOWN_OPEN",
  SET_PROJECT_DROPDOWN_OPEN: "SET_PROJECT_DROPDOWN_OPEN",
  SET_COMPANY_DROPDOWN_OPEN: "SET_COMPANY_DROPDOWN_OPEN",
  
  // Validation States
  SET_DESCRIPTION_VALID: "SET_DESCRIPTION_VALID",
  SET_DATE_VALID: "SET_DATE_VALID",
  SET_CATEGORY_VALID: "SET_CATEGORY_VALID",
  SET_CURRENCY_VALID: "SET_CURRENCY_VALID",
  SET_SUB_TOTAL_VALID: "SET_SUB_TOTAL_VALID",
  SET_TAX_VALID: "SET_TAX_VALID",
  SET_USER_VALID: "SET_USER_VALID",
  SET_CLIENT_VALID: "SET_CLIENT_VALID",
  SET_COMPANY_VALID: "SET_COMPANY_VALID",
  
  // Reset
  RESET_FORM: "RESET_FORM",
  RESET_VALIDATION: "RESET_VALIDATION",
  
  // Bulk Update
  SET_FORM_DATA: "SET_FORM_DATA",
  SET_VALIDATION_STATE: "SET_VALIDATION_STATE",
};

// Initial Form State
export const initialExpenseFormState = {
  // Form Fields
  description: "",
  expenseDate: null,
  category: "",
  currency: "",
  subTotal: "",
  tax: "",
  amount: 0,
  isBillable: true,
  user: "",
  client: "",
  project: "",
  company: "",
  note: "",
  fileAttachment: [],
  fileAttachmentName: "",
  
  // Dropdown Search Terms
  currencySearchTerm: "",
  userSearchTerm: "",
  clientSearchTerm: "",
  projectSearchTerm: "",
  companySearchTerm: "",
  
  // Dropdown Open States
  currencyDropdownOpen: false,
  categoryDropdownOpen: false,
  userDropdownOpen: false,
  clientDropdownOpen: false,
  projectDropdownOpen: false,
  companyDropdownOpen: false,
  
  // Validation States
  descriptionIsValid: false,
  dateIsValid: false,
  categoryIsValid: false,
  currencyIsValid: false,
  subTotalIsValid: false,
  taxIsValid: false,
  userIsValid: false,
  clientIsValid: false,
  companyIsValid: false,
};

/**
 * Expense Form Reducer
 * @param {Object} state - Current form state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New form state
 */
export const expenseFormReducer = (state, action) => {
  switch (action.type) {
    // Field Updates
    case EXPENSE_FORM_ACTION_TYPES.SET_DESCRIPTION:
      return {
        ...state,
        description: action.payload,
        descriptionIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_EXPENSE_DATE:
      return {
        ...state,
        expenseDate: action.payload,
        dateIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY:
      return {
        ...state,
        category: action.payload,
        categoryIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY:
      return {
        ...state,
        currency: action.payload,
        currencyIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_SUB_TOTAL:
      const newSubTotal = action.payload;
      const currentTax = parseFloat(state.tax) || 0;
      const newAmount = parseFloat(newSubTotal) + currentTax;
      return {
        ...state,
        subTotal: newSubTotal,
        amount: newAmount,
        subTotalIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_TAX:
      const newTax = action.payload;
      const currentSubTotal = parseFloat(state.subTotal) || 0;
      const calculatedAmount = currentSubTotal + (parseFloat(newTax) || 0);
      return {
        ...state,
        tax: newTax,
        amount: calculatedAmount,
        taxIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_AMOUNT:
      return {
        ...state,
        amount: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_IS_BILLABLE:
      return {
        ...state,
        isBillable: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_USER:
      return {
        ...state,
        user: action.payload,
        userIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CLIENT:
      return {
        ...state,
        client: action.payload,
        clientIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_PROJECT:
      return {
        ...state,
        project: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_COMPANY:
      return {
        ...state,
        company: action.payload,
        companyIsValid: false,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_NOTE:
      return {
        ...state,
        note: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_FILE_ATTACHMENT:
      return {
        ...state,
        fileAttachment: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_FILE_ATTACHMENT_NAME:
      return {
        ...state,
        fileAttachmentName: action.payload,
      };
    
    // Dropdown Search Terms
    case EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_SEARCH_TERM:
      return {
        ...state,
        currencySearchTerm: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_USER_SEARCH_TERM:
      return {
        ...state,
        userSearchTerm: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_SEARCH_TERM:
      return {
        ...state,
        clientSearchTerm: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_PROJECT_SEARCH_TERM:
      return {
        ...state,
        projectSearchTerm: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_SEARCH_TERM:
      return {
        ...state,
        companySearchTerm: action.payload,
      };
    
    // Dropdown Open States
    case EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_DROPDOWN_OPEN:
      return {
        ...state,
        currencyDropdownOpen: action.payload,
        currencySearchTerm: action.payload ? state.currencySearchTerm : "",
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY_DROPDOWN_OPEN:
      return {
        ...state,
        categoryDropdownOpen: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_USER_DROPDOWN_OPEN:
      return {
        ...state,
        userDropdownOpen: action.payload,
        userSearchTerm: action.payload ? state.userSearchTerm : "",
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_DROPDOWN_OPEN:
      return {
        ...state,
        clientDropdownOpen: action.payload,
        clientSearchTerm: action.payload ? state.clientSearchTerm : "",
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_PROJECT_DROPDOWN_OPEN:
      return {
        ...state,
        projectDropdownOpen: action.payload,
        projectSearchTerm: action.payload ? state.projectSearchTerm : "",
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_DROPDOWN_OPEN:
      return {
        ...state,
        companyDropdownOpen: action.payload,
        companySearchTerm: action.payload ? state.companySearchTerm : "",
      };
    
    // Validation Updates
    case EXPENSE_FORM_ACTION_TYPES.SET_DESCRIPTION_VALID:
      return {
        ...state,
        descriptionIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_DATE_VALID:
      return {
        ...state,
        dateIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY_VALID:
      return {
        ...state,
        categoryIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_VALID:
      return {
        ...state,
        currencyIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_SUB_TOTAL_VALID:
      return {
        ...state,
        subTotalIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_TAX_VALID:
      return {
        ...state,
        taxIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_USER_VALID:
      return {
        ...state,
        userIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_VALID:
      return {
        ...state,
        clientIsValid: action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_VALID:
      return {
        ...state,
        companyIsValid: action.payload,
      };
    
    // Bulk Updates
    case EXPENSE_FORM_ACTION_TYPES.SET_FORM_DATA:
      return {
        ...state,
        ...action.payload,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.SET_VALIDATION_STATE:
      return {
        ...state,
        ...action.payload,
      };
    
    // Reset Actions
    case EXPENSE_FORM_ACTION_TYPES.RESET_FORM:
      return {
        ...initialExpenseFormState,
      };
    
    case EXPENSE_FORM_ACTION_TYPES.RESET_VALIDATION:
      return {
        ...state,
        descriptionIsValid: false,
        dateIsValid: false,
        categoryIsValid: false,
        currencyIsValid: false,
        subTotalIsValid: false,
        taxIsValid: false,
        userIsValid: false,
        clientIsValid: false,
        companyIsValid: false,
      };
    
    default:
      return state;
  }
};
